import { createContext, useContext } from 'react'

import type { WindowShell } from './shell'
import type { WindowParams, WindowPresentation } from './types'

/**
 * Что окно знает о себе (карточки 265/274). Свой ключ и закрытие страница
 * берёт ОТСЮДА и не знает, как именно показана: на адресе или поверх экрана.
 * `navigate(...)` в обработчике закрытия и разбор `useParams` ради ключа в
 * окне — признак, что правило обошли.
 */
export interface WindowHandle {
  kind: string
  /** Identity окна, включая rid и сущность. */
  identity: string
  presentation: WindowPresentation
  /** Параметры окна: `rid`, ключ сущности. Неизменны, пока окно открыто. */
  params: WindowParams
  /**
   * Закрыть: на адресе — туда, откуда пришли; поверх экрана — снять со стопки. Если окно поставило сторожа
   * (`setCloseGuard`) и тот вернул `false`, окно остаётся: сторож сам спросит человека и закроет окно, сняв себя.
   */
  close: () => void
  /**
   * Сторож закрытия: вызывается на любое закрытие окна (крестик рамки, Esc, кнопка «Закрыть»). `true` — закрывать,
   * `false` — не закрывать, окно покажет подтверждение. `null` снимает сторожа; окно обязано снять его при выходе.
   */
  setCloseGuard?: (guard: (() => boolean) | null) => void
  /** Сменить это окно другим одним шагом. */
  replaceWith: (kind: string, params?: WindowParams) => void
  /** Передать работу окну на адресе `path`; «откуда пришли» переезжает к нему. */
  proceedTo: (path: string) => void
}

export const WindowContext = createContext<WindowHandle | null>(null)

export function useWindow(): WindowHandle {
  const handle = useContext(WindowContext)
  if (handle === null) throw new Error('useWindow() вызван вне окна: компонент нарисован не оболочкой окна')
  return handle
}

export const WindowShellContext = createContext<WindowShell | null>(null)

export function useWindowShell(): WindowShell {
  const shell = useContext(WindowShellContext)
  if (shell === null) throw new Error('Нет WindowShellContext: оберните приложение в WindowShellContext.Provider')
  return shell
}
